import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { OPERATIONS_LIVE_QUERY_KEY } from '@utils/api/back-office/operations/use-get-operations-live';

interface RetryStrandedSessionsRequest {
  session_ids: string[];
}

export const useRetryStrandedSessions = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const retryStrandedSessions = async (sessionIds: string[]) => {
    const payload: RetryStrandedSessionsRequest = { session_ids: sessionIds };
    // Remove try/catch to let Axios throw its error naturally
    const response = await Axios.post(`${POST_URL().retry}`, payload);
    return response.data;
  };

  return useMutation<void, AxiosError<BaseError>, string[]>(
    retryStrandedSessions,
    {
      onSuccess: () => {
        toast.success(t('stranded-sessions-retried-successfully'));
        // Invalidate stranded sessions and operations queries to refresh the data
        queryClient.invalidateQueries(['strandedSessions']);
        queryClient.invalidateQueries(['allStrandedSessionIds']);
        queryClient.invalidateQueries([OPERATIONS_LIVE_QUERY_KEY]);
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          t('failed-to-retry');
        toast.error(errorMessage);
      }
    }
  );
};
